export async function generateWaveform(
  audioFile: File,
  canvas: HTMLCanvasElement,
  color: string = '#4caf50'
): Promise<void> {
  const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  
  try {
    // Read the file as an array buffer
    const arrayBuffer = await audioFile.arrayBuffer();
    
    // Decode the audio data
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    
    // Get the canvas context
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get canvas context');
    
    // Set canvas size (550x60 as specified)
    canvas.width = 550;
    canvas.height = 60;
    
    // Clear the canvas
    ctx.fillStyle = '#2a2a2a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Get audio data
    const data = audioBuffer.getChannelData(0); // Get first channel
    const step = Math.ceil(data.length / canvas.width);
    const amp = canvas.height / 2;
    
    // Draw waveform
    ctx.beginPath();
    ctx.moveTo(0, amp);
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    
    for (let i = 0; i < canvas.width; i++) {
      let min = 1.0;
      let max = -1.0;
      
      for (let j = 0; j < step; j++) {
        const datum = data[(i * step) + j];
        if (datum < min) min = datum;
        if (datum > max) max = datum;
      }
      
      // Draw a vertical line for this sample
      const yMin = (1 + min) * amp;
      const yMax = (1 + max) * amp;
      
      if (i === 0) {
        ctx.moveTo(i, yMin);
      } else {
        ctx.lineTo(i, yMin);
      }
      ctx.lineTo(i, yMax);
    }
    
    ctx.stroke();
    
    // Draw a center line
    ctx.beginPath();
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 1;
    ctx.moveTo(0, amp);
    ctx.lineTo(canvas.width, amp);
    ctx.stroke();
    
  } finally {
    // Clean up audio context
    await audioContext.close();
  }
}

export function generateWaveformFromBuffer(
  audioBuffer: AudioBuffer,
  canvas: HTMLCanvasElement,
  color: string = '#4caf50'
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get canvas context');
  
  // Set canvas size
  canvas.width = 550;
  canvas.height = 60;
  
  // Clear the canvas
  ctx.fillStyle = '#1a1a1a';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  
  // Get audio data
  const data = audioBuffer.getChannelData(0); // Get first channel
  const step = Math.ceil(data.length / canvas.width);
  const amp = canvas.height / 2;
  
  // Draw waveform
  ctx.beginPath();
  ctx.moveTo(0, amp);
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  
  for (let i = 0; i < canvas.width; i++) {
    let min = 1.0;
    let max = -1.0;
    
    for (let j = 0; j < step; j++) {
      const datum = data[(i * step) + j];
      if (datum < min) min = datum;
      if (datum > max) max = datum;
    }
    
    // Draw a vertical line for this sample
    const yMin = (1 + min) * amp;
    const yMax = (1 + max) * amp;
    
    if (i === 0) {
      ctx.moveTo(i, yMin);
    } else {
      ctx.lineTo(i, yMin);
    }
    ctx.lineTo(i, yMax);
  }
  
  ctx.stroke();
  
  // Draw a center line
  ctx.beginPath();
  ctx.strokeStyle = '#333';
  ctx.lineWidth = 1;
  ctx.moveTo(0, amp);
  ctx.lineTo(canvas.width, amp);
  ctx.stroke();
}

export async function waveformToDataURL(
  audioFile: File,
  width: number = 550,
  height: number = 60,
  color: string = '#4caf50'
): Promise<string> {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  
  await generateWaveform(audioFile, canvas, color);
  
  return canvas.toDataURL('image/png');
}